import { Calendar, User } from 'lucide-react'
import { capitalize } from 'lodash'
import { initialFilter } from '../pages/AuditLogDashboard'
import type { AuditLogFilters } from '../pages/AuditLogDashboard'
import type { User as UserType } from '@/features/users/types/user'
import type { auditLogFilterSchema } from '@/schemas/filterSchemas'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DatePicker from '@/components/custom/date-picker'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useCleanQueryStates } from '@/hooks'
import { parseLocalDateString, toLocalDateString } from '@/utils/timeZoneUtils'

type AuditLogFiltersProps = {
  onFilter: React.Dispatch<React.SetStateAction<AuditLogFilters>>
  goToFirstPage: () => void
  logUsers: Array<UserType>
  logActions: Array<string>
}

export function AuditLogFilters({
  onFilter,
  goToFirstPage,
  logUsers,
  logActions,
}: AuditLogFiltersProps) {
  const [filters, setFilters] =
    useCleanQueryStates<typeof auditLogFilterSchema>('/authed/audit')

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <Label className="text-sm font-medium mb-1 block">
            <User className="h-4 w-4 inline mr-1" />
            User
          </Label>
          <Select
            value={filters.userId}
            onValueChange={(value: string) => {
              setFilters((prev) => ({ ...prev, userId: value }))
              onFilter((prev) => ({ ...prev, userId: value }))
              goToFirstPage()
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {logUsers.map((u) => (
                <SelectItem key={u._id} value={u._id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-1 block">Action</label>
          <Select
            value={filters.action || 'all'}
            onValueChange={(value: string) => {
              setFilters((prev) => ({ ...prev, action: value }))
              onFilter((prev) => ({ ...prev, action: value }))
              goToFirstPage()
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {logActions.map((action) => (
                <SelectItem key={action} value={action}>
                  {capitalize(action)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <DatePicker
            field={{
              state: {
                value: filters.startDate
                  ? parseLocalDateString(filters.startDate)
                  : undefined,
                meta: {
                  isTouched: false,
                  isValid: true,
                  errors: [],
                  isValidating: false,
                },
              },
              handleChange: (date: Date | undefined) => {
                const dateStr = date ? toLocalDateString(date) : ''
                setFilters((prev) => ({ ...prev, startDate: dateStr }))
                onFilter((prev) => ({ ...prev, startDate: dateStr }))
                goToFirstPage()
              },
              name: 'startDate',
            }}
            label={
              <>
                <Calendar className="h-4 w-4 inline mr-1" /> Start Date
              </>
            }
            placeholder="Select date"
            showClearButton
          />
        </div>

        <div>
          <DatePicker
            field={{
              state: {
                value: filters.endDate
                  ? parseLocalDateString(filters.endDate)
                  : undefined,
                meta: {
                  isTouched: false,
                  isValid: true,
                  errors: [],
                  isValidating: false,
                },
              },
              handleChange: (date: Date | undefined) => {
                const dateStr = date ? toLocalDateString(date) : ''
                setFilters((prev) => ({ ...prev, endDate: dateStr }))
                onFilter((prev) => ({ ...prev, endDate: dateStr }))
                goToFirstPage()
              },
              name: 'endDate',
            }}
            placeholder="Select date"
            label={
              <>
                <Calendar className="h-4 w-4 inline mr-1" /> End Date
              </>
            }
            showClearButton
            minDate={
              filters.startDate
                ? parseLocalDateString(filters.startDate)
                : undefined
            }
          />
        </div>
        <div className="flex items-end gap-2 mt-2">
          <Button
            type="button"
            onClick={() => {
              onFilter(initialFilter)
              setFilters(initialFilter)
              goToFirstPage()
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>
      <Separator className="col-span-1 lg:col-span-5" />
    </>
  )
}
